import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import { AutocompleteInput, ReferenceInput, required } from "react-admin";

import { useRecipientSelection } from "@/containers/whatsapp-broadcasts/context";
import {
  useIsFmbOutstandingBroadcastEnabled,
  useIsMuttavatteenOutstandingBroadcastEnabled,
  useIsSabilOutstandingBroadcastEnabled,
} from "./hooks";
import {
  DEFAULT_EVENT_RSVP_TEMPLATE_NAME,
  DEFAULT_FMB_OUTSTANDING_MIN_PERCENT,
  DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME,
  DEFAULT_MIQAAT_RSVP_TEMPLATE_NAME,
  deriveBroadcastCategory,
  derivePrimaryModeTabValue,
  FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
  getMuttavatteenOutstandingTabFieldUpdates,
  getSabilOutstandingTabFieldUpdates,
  MUTTAVATTEEN_MONTHLY_OUTSTANDING_TAB,
  MUTTAVATTEEN_YEARLY_OUTSTANDING_TAB,
  resolveBroadcastModeTabValue,
  SABIL_CHULA_OUTSTANDING_TAB,
  SABIL_ESTABLISHMENT_OUTSTANDING_TAB,
  type BroadcastKind,
  type ModeTabValue,
  type OutstandingTabFieldUpdates,
} from "./utils";
import { createDefaultMiqaatRsvpFilterEnvelope } from "./miqaat-rsvp-filter-criteria";
import MiqaatSelector from "./MiqaatSelector";

type BroadcastModeFieldsProps = {
  activeStep?: number;
  onResetToStep0?: () => void;
  onMiqaatModeTemplateStep?: (isValid: boolean, errorMessage: string | null) => void;
  onEventModeTemplateStep?: (isValid: boolean, errorMessage: string | null) => void;
  onFmbOutstandingModeTemplateStep?: (isValid: boolean, errorMessage: string | null) => void;
  onSabilOutstandingModeTemplateStep?: (isValid: boolean, errorMessage: string | null) => void;
  onMuttavatteenOutstandingModeTemplateStep?: (
    isValid: boolean,
    errorMessage: string | null
  ) => void;
};

const clearOutstandingAudienceFields = (
  setValue: ReturnType<typeof useFormContext>["setValue"]
) => {
  setValue("filterCriteria", null, { shouldDirty: true });
  setValue("recipientItsIds", null, { shouldDirty: true });
  setValue("recipientCsvData", null, { shouldDirty: true });
  setValue("csvColumnHeaders", [], { shouldDirty: true });
  setValue("thaliTypeIds", [], { shouldDirty: true });
  setValue("deliveryScheduleProfileIds", [], { shouldDirty: true });
  setValue("itsIds", [], { shouldDirty: true });
  setValue("excludeItsIds", [], { shouldDirty: true });
  setValue("sabilType", null, { shouldDirty: true });
  setValue("selectedRecipientPhones", null, { shouldDirty: true });
  setValue("recipientPhoneNumbers", null, { shouldDirty: true });
  setValue("recipientsPreviewed", false, { shouldDirty: true });
};

export default function BroadcastModeFields({
  activeStep = 0,
  onResetToStep0,
  onMiqaatModeTemplateStep,
  onEventModeTemplateStep,
  onFmbOutstandingModeTemplateStep,
  onSabilOutstandingModeTemplateStep,
  onMuttavatteenOutstandingModeTemplateStep,
}: BroadcastModeFieldsProps) {
  const { setValue, control } = useFormContext();
  const { recipientCount, filterPreviewed, updateSelectedPhones, updatePreviewStatus } =
    useRecipientSelection();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingTargetTab, setPendingTargetTab] = useState<ModeTabValue | null>(null);

  const kind = (useWatch({ control, name: "broadcastKind" }) as string | undefined) ?? "TEMPLATE";
  const sabilType = useWatch({ control, name: "sabilType" }) as string | null | undefined;
  const fmbOutstandingEnabled = useIsFmbOutstandingBroadcastEnabled();
  const sabilOutstandingEnabled = useIsSabilOutstandingBroadcastEnabled();
  const muttavatteenOutstandingEnabled = useIsMuttavatteenOutstandingBroadcastEnabled();

  useEffect(() => {
    if (fmbOutstandingEnabled || kind !== "FMB_OUTSTANDING") return;
    setValue("broadcastKind", "TEMPLATE", { shouldDirty: false });
    onFmbOutstandingModeTemplateStep?.(false, null);
  }, [fmbOutstandingEnabled, kind, onFmbOutstandingModeTemplateStep, setValue]);

  useEffect(() => {
    if (sabilOutstandingEnabled || kind !== "SABIL_OUTSTANDING") return;
    setValue("broadcastKind", "TEMPLATE", { shouldDirty: false });
    onSabilOutstandingModeTemplateStep?.(false, null);
  }, [sabilOutstandingEnabled, kind, onSabilOutstandingModeTemplateStep, setValue]);

  useEffect(() => {
    if (muttavatteenOutstandingEnabled || kind !== "MUTTAVATTEEN_OUTSTANDING") return;
    setValue("broadcastKind", "TEMPLATE", { shouldDirty: false });
    onMuttavatteenOutstandingModeTemplateStep?.(false, null);
  }, [muttavatteenOutstandingEnabled, kind, onMuttavatteenOutstandingModeTemplateStep, setValue]);

  const tabsValue: ModeTabValue = resolveBroadcastModeTabValue({
    kind,
    sabilType,
    fmbOutstandingEnabled,
    sabilOutstandingEnabled,
    muttavatteenOutstandingEnabled,
  });
  const primaryValue = derivePrimaryModeTabValue(tabsValue);
  const activeCategory = deriveBroadcastCategory(primaryValue);
  const anyOutstandingEnabled =
    fmbOutstandingEnabled || sabilOutstandingEnabled || muttavatteenOutstandingEnabled;

  const resetTemplateStepFlags = () => {
    onMiqaatModeTemplateStep?.(false, null);
    onEventModeTemplateStep?.(false, null);
    onFmbOutstandingModeTemplateStep?.(false, null);
    onSabilOutstandingModeTemplateStep?.(false, null);
    onMuttavatteenOutstandingModeTemplateStep?.(false, null);
  };

  /**
   * Shared by both Sabil (Chula/Establishment) and Mutawatteen (Monthly/Yearly) tabs — each pair
   * is split into separate tabs so an ITS due in both never gets one mixed send.
   */
  const applyOutstandingTabUpdates = (
    updates: OutstandingTabFieldUpdates,
    onStep: ((isValid: boolean, errorMessage: string | null) => void) | undefined
  ) => {
    setValue("broadcastKind", updates.broadcastKind, { shouldDirty: true });
    setValue("templateName", updates.templateName, { shouldDirty: true });
    setValue("parameters", {}, { shouldDirty: true });
    setValue("miqaatId", null, { shouldDirty: true });
    setValue("eventId", null, { shouldDirty: true });
    clearOutstandingAudienceFields(setValue);
    setValue("sabilType", updates.sabilType, { shouldDirty: true });
    setValue("minOutstandingPercent", updates.minOutstandingPercent, { shouldDirty: true });
    setValue("notifyMode", updates.notifyMode, { shouldDirty: true });
    resetTemplateStepFlags();
    onStep?.(true, null);
  };

  const executeModeSwitch = (targetTab: ModeTabValue) => {
    if (
      targetTab === SABIL_CHULA_OUTSTANDING_TAB ||
      targetTab === SABIL_ESTABLISHMENT_OUTSTANDING_TAB
    ) {
      const type = targetTab === SABIL_ESTABLISHMENT_OUTSTANDING_TAB ? "ESTABLISHMENT" : "CHULA";
      applyOutstandingTabUpdates(
        getSabilOutstandingTabFieldUpdates(type),
        onSabilOutstandingModeTemplateStep
      );
      return;
    }

    if (
      targetTab === MUTTAVATTEEN_MONTHLY_OUTSTANDING_TAB ||
      targetTab === MUTTAVATTEEN_YEARLY_OUTSTANDING_TAB
    ) {
      const type =
        targetTab === MUTTAVATTEEN_YEARLY_OUTSTANDING_TAB ? "MUTTAVATTEEN_YEARLY" : "MUTTAVATTEEN";
      applyOutstandingTabUpdates(
        getMuttavatteenOutstandingTabFieldUpdates(type),
        onMuttavatteenOutstandingModeTemplateStep
      );
      return;
    }

    const nextKind = targetTab as BroadcastKind;
    setValue("broadcastKind", nextKind, { shouldDirty: true });

    if (nextKind === "MIQAAT_RSVP") {
      setValue("templateName", DEFAULT_MIQAAT_RSVP_TEMPLATE_NAME, { shouldDirty: true });
      setValue("parameters", {}, { shouldDirty: true });
      setValue("filterCriteria", createDefaultMiqaatRsvpFilterEnvelope(), {
        shouldDirty: true,
      });
      setValue("recipientItsIds", null, { shouldDirty: true });
      setValue("recipientCsvData", null, { shouldDirty: true });
      setValue("csvColumnHeaders", [], { shouldDirty: true });
      setValue("selectedRecipientPhones", null, { shouldDirty: true });
      setValue("recipientPhoneNumbers", null, { shouldDirty: true });
      setValue("recipientsPreviewed", false, { shouldDirty: true });
      setValue("eventId", null, { shouldDirty: true });
      setValue("sabilType", null, { shouldDirty: true });
      setValue("minOutstandingPercent", null, { shouldDirty: true });
      setValue("notifyMode", null, { shouldDirty: true });
      resetTemplateStepFlags();
      onMiqaatModeTemplateStep?.(true, null);
    } else if (nextKind === "EVENT_RSVP") {
      setValue("templateName", DEFAULT_EVENT_RSVP_TEMPLATE_NAME, { shouldDirty: true });
      setValue("parameters", {}, { shouldDirty: true });
      setValue("miqaatId", null, { shouldDirty: true });
      setValue("filterCriteria", null, { shouldDirty: true });
      setValue("recipientItsIds", null, { shouldDirty: true });
      setValue("recipientCsvData", null, { shouldDirty: true });
      setValue("csvColumnHeaders", [], { shouldDirty: true });
      setValue("selectedRecipientPhones", null, { shouldDirty: true });
      setValue("recipientPhoneNumbers", null, { shouldDirty: true });
      setValue("recipientsPreviewed", false, { shouldDirty: true });
      setValue("sabilType", null, { shouldDirty: true });
      setValue("minOutstandingPercent", null, { shouldDirty: true });
      setValue("notifyMode", null, { shouldDirty: true });
      resetTemplateStepFlags();
      onEventModeTemplateStep?.(true, null);
    } else if (nextKind === "FMB_OUTSTANDING") {
      setValue("templateName", DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME, { shouldDirty: true });
      setValue("parameters", {}, { shouldDirty: true });
      setValue("miqaatId", null, { shouldDirty: true });
      setValue("eventId", null, { shouldDirty: true });
      clearOutstandingAudienceFields(setValue);
      setValue("minOutstandingPercent", DEFAULT_FMB_OUTSTANDING_MIN_PERCENT, {
        shouldDirty: true,
      });
      setValue("notifyMode", FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY, {
        shouldDirty: true,
      });
      resetTemplateStepFlags();
      onFmbOutstandingModeTemplateStep?.(true, null);
    } else {
      // TEMPLATE: clear any RSVP/outstanding state
      setValue("templateName", null, { shouldDirty: true });
      setValue("parameters", {}, { shouldDirty: true });
      setValue("filterCriteria", null, { shouldDirty: true });
      setValue("recipientItsIds", null, { shouldDirty: true });
      setValue("recipientCsvData", null, { shouldDirty: true });
      setValue("csvColumnHeaders", [], { shouldDirty: true });
      setValue("selectedRecipientPhones", null, { shouldDirty: true });
      setValue("recipientPhoneNumbers", null, { shouldDirty: true });
      setValue("recipientsPreviewed", false, { shouldDirty: true });
      setValue("miqaatId", null, { shouldDirty: true });
      setValue("eventId", null, { shouldDirty: true });
      setValue("sabilType", null, { shouldDirty: true });
      setValue("minOutstandingPercent", null, { shouldDirty: true });
      setValue("notifyMode", null, { shouldDirty: true });
      resetTemplateStepFlags();
    }
  };

  const requestModeChange = (targetTab: ModeTabValue) => {
    if (targetTab === tabsValue) return;

    const hasProgress = activeStep > 0 || recipientCount > 0 || filterPreviewed;
    if (hasProgress) {
      setPendingTargetTab(targetTab);
      setConfirmDialogOpen(true);
    } else {
      executeModeSwitch(targetTab);
    }
  };

  const handleConfirmModeChange = () => {
    if (pendingTargetTab) {
      onResetToStep0?.();
      updateSelectedPhones([]);
      updatePreviewStatus(false);
      executeModeSwitch(pendingTargetTab);
    }
    setConfirmDialogOpen(false);
    setPendingTargetTab(null);
  };

  const handleCancelModeChange = () => {
    setConfirmDialogOpen(false);
    setPendingTargetTab(null);
  };

  const handleCategoryChange = (_: unknown, category: "CUSTOM" | "RSVP" | "OUTSTANDING") => {
    if (!category || category === activeCategory) return;

    if (category === "CUSTOM") {
      requestModeChange("TEMPLATE");
    } else if (category === "RSVP") {
      requestModeChange("MIQAAT_RSVP");
    } else if (category === "OUTSTANDING") {
      if (fmbOutstandingEnabled) {
        requestModeChange("FMB_OUTSTANDING");
      } else if (sabilOutstandingEnabled) {
        requestModeChange(SABIL_CHULA_OUTSTANDING_TAB);
      } else if (muttavatteenOutstandingEnabled) {
        requestModeChange(MUTTAVATTEEN_MONTHLY_OUTSTANDING_TAB);
      }
    }
  };

  return (
    <Box sx={{ mb: 2, width: "100%", minWidth: 0 }}>
      {/* Primary 3-Category Tabs */}
      <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <Tabs
          value={activeCategory}
          onChange={handleCategoryChange}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            width: "100%",
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Custom broadcast" value="CUSTOM" />
          <Tab label="RSVP & Events" value="RSVP" />
          {anyOutstandingEnabled && <Tab label="Outstanding reminders" value="OUTSTANDING" />}
        </Tabs>
      </Box>

      {/* Secondary Sub-Selectors */}
      {activeCategory === "RSVP" && (
        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            RSVP type:
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={primaryValue}
            onChange={(_, v: BroadcastKind | null) => v && requestModeChange(v)}
          >
            <ToggleButton value="MIQAAT_RSVP">Miqaat RSVP</ToggleButton>
            <ToggleButton value="EVENT_RSVP">Niyaaz Event RSVP</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {activeCategory === "OUTSTANDING" && anyOutstandingEnabled && (
        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            Dues type:
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={tabsValue}
            onChange={(_, v: ModeTabValue | null) => v && requestModeChange(v)}
          >
            {fmbOutstandingEnabled && (
              <ToggleButton value="FMB_OUTSTANDING">FMB Outstanding</ToggleButton>
            )}
            {sabilOutstandingEnabled && (
              <ToggleButton value={SABIL_CHULA_OUTSTANDING_TAB}>Sabil (Chula)</ToggleButton>
            )}
            {sabilOutstandingEnabled && (
              <ToggleButton value={SABIL_ESTABLISHMENT_OUTSTANDING_TAB}>
                Sabil (Establishment)
              </ToggleButton>
            )}
            {muttavatteenOutstandingEnabled && (
              <ToggleButton value={MUTTAVATTEEN_MONTHLY_OUTSTANDING_TAB}>
                Mutawatteen (Monthly)
              </ToggleButton>
            )}
            {muttavatteenOutstandingEnabled && (
              <ToggleButton value={MUTTAVATTEEN_YEARLY_OUTSTANDING_TAB}>
                Mutawatteen (Yearly)
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>
      )}

      {kind === "MIQAAT_RSVP" && (
        <Box sx={{ mt: 2, width: "100%", maxWidth: 620 }}>
          <MiqaatSelector />
        </Box>
      )}

      {kind === "EVENT_RSVP" && (
        <Box sx={{ mt: 2, width: "100%", maxWidth: 560 }}>
          <ReferenceInput source="eventId" reference="events" perPage={200}>
            <AutocompleteInput
              optionText="name"
              label="Niyaaz event"
              validate={required()}
              fullWidth
            />
          </ReferenceInput>
        </Box>
      )}

      {/* Confirmation Alert Dialog on Broadcast Mode / Tab Change */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelModeChange}
        aria-labelledby="switch-broadcast-mode-title"
        aria-describedby="switch-broadcast-mode-description"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="switch-broadcast-mode-title" sx={{ fontWeight: 600 }}>
          Switch Broadcast Type?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="switch-broadcast-mode-description">
            Switching broadcast type will discard your current progress (selected recipients,
            template, and parameters) and return you to step 1.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelModeChange} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleConfirmModeChange} color="primary" variant="contained" autoFocus>
            Switch & Start Over
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
